import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import "./Autocomplete.scss";

import _ from "lodash";

const SORT = [];
const PAGE = {
  skip: 0,
  take: 10,
};

let _timer;

/**
 * Autocomplete input
 * @module
 * @param {string} textField - Text field
 * @param {string} [valueField] - Value field, used for regex
 * @param {object} [selected] - Selected item
 * @param {func} onChange - Function invoked on field change
 * @param {array} data - All data
 * @param {bool} isLoading - Loading
 * @param {func} onSelect - Function invoked on item select
 * @param {func} onFocus - Fires each time the user focuses the AutoComplete.
 * @param {object} [filter] - Filter
 * @param {func} [onFilter] - On filter Function
 * @param {bool} [required] - Field is required
 * @param {bool} [disabled] - Field is disabled
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {bool} [autofocus] - Should input be focused?
 * @param {string} [textFilter] - Text filter
 * @param {string} [placeholder] - Placeholder for input
 * @param {number} [tabIndex] - Tabindex for input
 * @param {string} [name] - field name
 * @param {array} [sort] - Array of sorts
 */
const AutocompleteComponent = (props) => {
  const autoCompleteRef = useRef(null);
  const data = props.allDataItem ? [props.allDataItem, ...props.data] : props.data;

  const buildFilter = (value) => {
    if (!value) {
      return {
        filters: [],
      };
    }
    return {
      logic: "and",
      filters: [
        {
          field: props.textField,
          operator: "contains",
          value,
          ignoreCase: true,
        },
      ],
    };
  };

  const onClose = async () => {
    await props.onBlur;
    const value = autoCompleteRef.current._input.value;
    if (value && !props.selected) {
      data.forEach((object) => {
        if (_.get(object, props.textField.split('.')) === value) {
          props.onSelect(object, props.name);
        }
      });
    }
    if (props.onBlur) {
      props.onBlur(props.selected);
    }
  };

  const onChange = async (event) => {
    const filter = buildFilter(event.target.value);

    if (props.onFilter) {
      props.onFilter(filter);
    }

    const eventType = event.nativeEvent.type;
    if (
      (eventType === "click" && event.target.value !== '') ||
      (eventType === "keydown" && event.nativeEvent.keyCode === 13)
    ) {
      const value = data.find(
        (x) => _.get(x, props.textField.split('.')) === event.target.value
      );
      await props.onSelect(value, props.name);
      if (props.onBlur) {
        await props.onBlur(value);
      }
    } else {
      if ((props.selected !== undefined && props.selected !== null) || props.allDataItem) {
        await props.onSelect(null, props.name, event);
        if (props.onBlur) {
          await props.onBlur(null);
        }
      }

      if (_timer) {
        clearTimeout(_timer);
      }
      _timer = setTimeout(async () => {
        props.onChange(filter, props.sort ?? SORT, PAGE);
      }, 500);
    }
  };

  const buildValue = () => {
    if (props.textFilter !== undefined) {
      return props.textFilter;
    }

    if (props.selected) {
      if (props.valueField) {
        let toReplace = props.valueField.match(/{([^}]+)}/g);
        if (!toReplace) {
          return _.get(props.selected, props.valueField.split('.'))
        }
        let finalString = props.valueField;
        toReplace.forEach((element) => {
          let propName = element.replace(/[{}]/g, "");
          const replaceWith = _.get(props.selected, propName);
          finalString = finalString.replace(
            element,
            replaceWith != null ? replaceWith : ""
          );
        });
        return finalString.trim();
      } else {
        return _.get(props.selected, props.textField.split('.'))
      }
    }

    const filter = props.filter;
    if (!filter) {
      return undefined;
    }
    const f = filter.filters.find((x) => x.field === props.textField);
    return f && f.value ? f.value.toString() : "";
  };

  useEffect(() => {
    if (props.autofocus) {
      autoCompleteRef.current._input.focus();
    }
  }, [props.autofocus]);

  const className = "autocomplete " + (props.disabled ? "disabled-field " : "") + (props.className ? props.className : "");

  return (
    <AutoComplete
      className={className}
      validationMessage={props.validationMessage || "Povinné"}
      data={data}
      onChange={onChange}
      onBlur={onClose}
      textField={props.textField}
      loading={props.isLoading}
      value={buildValue()}
      required={props.required}
      disabled={props.disabled}
      placeholder={props.placeholder}
      ref={autoCompleteRef}
      tabIndex={props.tabIndex}
      footer={props.footer}
      onFocus={props.onFocus}
    />
  );
};

AutocompleteComponent.propTypes = {
  className: PropTypes.string,
  textField: PropTypes.string.isRequired,
  valueField: PropTypes.string,
  primaryField: PropTypes.string.isRequired,
  selected: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  filter: PropTypes.object,
  onFilter: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  validationMessage: PropTypes.string,
  autofocus: PropTypes.bool,
  textFilter: PropTypes.string,
  placeholder: PropTypes.string,
  tabIndex: PropTypes.number,
  name: PropTypes.string,
  allDataItem: PropTypes.object,
  sort: PropTypes.array
};

export default AutocompleteComponent;
